import { State, Action, StateContext, Selector, Store } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { 
  SetEvent, 
  ResetEvent, 
  UpdateEventField
} from './event.actions';
import { BaseEvent, SrEvent } from '../../models/sr-event.model';
import { FORM_FIELDS } from '../../features/event/constants/event-editor.constants';

export interface EventStateModel {
  base: BaseEvent;
  'sr-event': SrEvent;
  // Form state
  isFormValid: boolean;
  isFormDirty: boolean;
  lastUpdated: number;
}

@State<EventStateModel>({
  name: 'event',
  defaults: {
    base: {
      workers: [],
      clients: [],
      plannedFrom: null,
      plannedTo: null,
      realizedFrom: null,
      realizedTo: null,
      rrule: '',
      recurrenceException: null,
      recurrenceParentId: null,
      recurrenceEnd: null,
      statusId: 1,
      allocationId: 0,
    },
    'sr-event': {
      title: '',
      typeOfContact: 'phone',
      description: '',
    },
    isFormValid: false,
    isFormDirty: false,
    lastUpdated: 0,
  },
})
@Injectable()
export class EventState {
  constructor(private store: Store) {}

  @Selector()
  static event(state: EventStateModel) {
    return state;
  }

  @Selector()
  static baseData(state: EventStateModel) {
    return state.base;
  }

  @Selector()
  static srEventData(state: EventStateModel) {
    return state['sr-event'];
  }

  @Selector()
  static selectClients(state: EventStateModel) {
    return state.base?.clients || [];
  }

  @Selector()
  static selectWorkers(state: EventStateModel) {
    return state.base?.workers || [];
  }

  @Selector()
  static isFormValid(state: EventStateModel) {
    return state.isFormValid;
  }

  @Selector()
  static isFormDirty(state: EventStateModel) {
    return state.isFormDirty;
  }

  @Action(SetEvent)
  setEvent(ctx: StateContext<EventStateModel>, action: SetEvent) {
    // Patch allocationId from user state if present (create mode)
    const user = this.store.selectSnapshot((state: any) => state.user?.activeAllocationCurrentUser);
    const allocationId = user?.allocationId ?? 0;
    
    const eventPatch = {
      ...action.event,
      base: {
        ...action.event.base,
        allocationId,
      },
      lastUpdated: Date.now(),
    };
    
    ctx.patchState(eventPatch);
  }

  @Action(UpdateEventField)
  updateEventField(ctx: StateContext<EventStateModel>, action: UpdateEventField) {
    const currentState = ctx.getState();
    const { section, field, value } = action;
    
    // Special handling for allocationId - get from user state if not provided
    let finalValue = value;
    if (field === FORM_FIELDS.ALLOCATION_ID && (value === null || value === undefined || value === 0)) {
      const user = this.store.selectSnapshot((state: any) => state.user?.activeAllocationCurrentUser);
      finalValue = user?.allocationId ?? 1; // Default to 1 if no user allocation
    }
    
    const updatedState = {
      ...currentState,
      [section]: {
        ...currentState[section],
        [field]: finalValue,
      },
      lastUpdated: Date.now(),
    };
    
    ctx.patchState(updatedState);
  }

  @Action(ResetEvent)
  resetEvent(ctx: StateContext<EventStateModel>) {
    const user = this.store.selectSnapshot((state: any) => state.user?.activeAllocationCurrentUser);
    const allocationId = user?.allocationId ?? 0;
    
    ctx.setState({
      base: {
        workers: [],
        clients: [],
        plannedFrom: null,
        plannedTo: null,
        realizedFrom: null,
        realizedTo: null,
        rrule: '',
        recurrenceException: null,
        recurrenceParentId: null,
        recurrenceEnd: null,
        statusId: 1,
        allocationId,
      },
      'sr-event': {
        title: '',
        typeOfContact: 'phone',
        description: '',
      },
      isFormValid: false,
      isFormDirty: false,
      lastUpdated: Date.now(),
    });
  }

} 